import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Clock, Loader2, RefreshCw, Sunrise, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { MetricCard } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtNumber, fmtRelative } from '@/shared/lib/format'
import { overview, pinReport, type Overview, type Proposal } from './api'
import { RUN_STATUS_LABEL, isLiveRun, kes, usd } from './vocab'
import { ReportView } from './components/ReportView'

/**
 * The landing screen: what arrived, and whether this thing is actually working.
 *
 * ## Two honest numbers at the top
 *
 * Most AI consoles open with a hero and a prompt box. This opens with the pinned
 * morning brief if there is one — and, if there is not, with the reason. An
 * advisory console nobody opens is worth nothing, and the fix is for the answer
 * to arrive rather than wait.
 *
 * ## The vendor-diversity banner
 *
 * When only one vendor is configured, the red team has nobody else to be, so no
 * report on this deployment can claim HIGH confidence. That is a one-line fix an
 * operator can make and would otherwise never learn about, so it is stated here
 * rather than buried in a settings page.
 */
export function BriefPage() {
  const [data, setData] = useState<Overview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await overview())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Poll only while something is actually running. A console that polls forever
  // is a console that keeps a laptop's fan on all afternoon for nothing.
  useEffect(() => {
    if (!data || data.runningNow === 0) return
    const timer = setInterval(() => { void load() }, 4000)
    return () => clearInterval(timer)
  }, [data, load])

  const refresh = async () => {
    setBusy(true)
    await load()
    setBusy(false)
  }

  const onPin = async (pinned: boolean) => {
    if (!data?.pinnedBrief) return
    await pinReport(data.pinnedBrief.id, pinned)
    await load()
  }

  const onProposalChanged = (_p: Proposal) => { void load() }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-erp-lg font-semibold">Axis Advisory</h1>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Machine analysis of this platform&rsquo;s own data — built from a permission-filtered
            brief, argued over by models from different companies, and never allowed to change
            anything on its own.
          </p>
        </div>
        <button type="button" onClick={() => void refresh()}
          className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {data === null && !error && <LoadingBlock />}

      {data && (
        <>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="Runs today" value={fmtNumber(data.runsToday)} />
            <MetricCard label="Running now" value={fmtNumber(data.runningNow)} />
            <MetricCard label="Proposals waiting" value={fmtNumber(data.openProposals)} />
            <MetricCard label="Spent this month"
              value={usd(data.budget.spentUsd, 2)}
              sub={`${kes(data.budget.spentUsd, data.budget.usdToKes)} of ${
                usd(data.budget.capUsd, 2)} capped`} />
            <MetricCard label="Models reachable"
              value={`${data.reachableModels} / ${data.configuredModels}`} />
          </div>

          {data.reachableModels === 0 && (
            <Note tone="bad">
              <span className="font-medium">No model provider is configured. </span>
              Nothing in this module can run until an account is registered under Integrations
              with an API key stored against it. The catalogue lists {data.configuredModels}{' '}
              models; none of them has credentials behind it.
              {' '}<Link to="/integrations" className="underline">Open Integrations</Link>
            </Note>
          )}

          {data.reachableModels > 0 && !data.vendorDiversityPossible && (
            <Note tone="info">
              <span className="font-medium">Only one vendor is configured
                ({data.configuredVendors.join(', ')}). </span>
              The red team has to be a different company than the analyst, so it cannot run —
              and every report produced here is capped at MEDIUM confidence and says why.
              Registering a second provider is what unlocks HIGH.
            </Note>
          )}

          {data.pinnedBrief ? (
            <section className="rounded-md border border-border p-3">
              <div className="mb-2 flex items-center gap-1.5 text-erp-xs uppercase tracking-wide
                text-muted-foreground">
                <Sunrise className="h-3.5 w-3.5" /> Pinned brief
              </div>
              <ReportView report={data.pinnedBrief} onPin={(p) => void onPin(p)}
                onProposalChanged={onProposalChanged} />
            </section>
          ) : (
            <Note tone="info">
              <span className="font-medium">Nothing is pinned. </span>
              A scheduled briefing pins itself here every morning — set one up on the Usage tab,
              or run a playbook from the Console and pin the report you want the room to see.
            </Note>
          )}

          <section>
            <h2 className="text-erp-sm font-medium mb-1.5">Recent runs</h2>
            {data.recentRuns.length === 0 ? (
              <p className="text-erp-xs text-muted-foreground">
                Nothing has run yet. The Console has {data.playbooks.length} playbooks ready.
              </p>
            ) : (
              <ul className="space-y-1">
                {data.recentRuns.map((run) => (
                  <li key={run.id}>
                    <Link to={`/advisory/runs/${run.id}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-md border
                        border-border px-2.5 py-1.5 hover:border-chocolate/50">
                      <span className={cn('text-erp-xs',
                        run.status === 'FAILED' && 'text-red-700 dark:text-red-400',
                        run.status === 'BLOCKED_BUDGET' && 'text-amber-700 dark:text-amber-400',
                        isLiveRun(run.status) && 'text-chocolate',
                      )}>
                        {isLiveRun(run.status) && (
                          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                        )}
                        {RUN_STATUS_LABEL[run.status] ?? run.status}
                      </span>
                      <span className="text-erp-sm min-w-0 flex-1 truncate">{run.title}</span>
                      <span className="text-erp-xs text-muted-foreground inline-flex
                        items-center gap-1">
                        <Users className="h-3 w-3" />{run.playbookCode}
                      </span>
                      <span className="text-erp-xs text-muted-foreground inline-flex
                        items-center gap-1">
                        <Clock className="h-3 w-3" />{fmtRelative(run.requestedAt)}
                      </span>
                      <span className="text-erp-xs text-muted-foreground tabular-nums">
                        {usd(run.costUsd)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {!data.mediaEnabled && (
            <p className="text-erp-xs text-muted-foreground/70 flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              Synthetic media generation is off for this deployment. Content briefs are still
              written; nothing is rendered until somebody turns it on deliberately.
            </p>
          )}
        </>
      )}
    </div>
  )
}
